import React, { Component } from 'react';
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Button, FormGroup, Label, Input, Alert
} from 'reactstrap';
import { post, put } from '../components/hooks/apiFetch';
import PasswordInput from './PasswordInput';

class PasswordResetModal extends Component {
    state = {
        email: '',
        code: '',
        newPassword: '',
        confirmPassword: '',
        showNewPassword: false,
        showConfirmPassword: false,
        step: 1,
        isLoading: false,
        error: null,
        success: false,
        canResend: true,
        countdown: 0,
        lastSentTime: null
    };

    countdownInterval = null;

    componentWillUnmount() {
        clearInterval(this.countdownInterval);
    }

    handleInputChange = (e) => {
        const { name, value } = e.target

        if (name === 'email') {
            const atIndex = value.indexOf('@');
            if (atIndex >= 0) {
                const beforeAt = value.substring(0, atIndex);
                const afterAt = value.substring(atIndex).toLowerCase();
                this.setState({
                    [name]: beforeAt + afterAt,
                    error: null
                });
                return;
            }
        }

        this.setState({ [name]: value, error: null });
    };

    toggleShowPassword = (field) => {
        this.setState(prevState => ({
            [`show${field}`]: !prevState[`show${field}`]
        }));
    };

    startCountdown = () => {
        const countdownTime = 5 * 60;
        this.setState({
            canResend: false,
            countdown: countdownTime,
            lastSentTime: new Date()
        });

        this.countdownInterval = setInterval(() => {
            this.setState(prevState => {
                if (prevState.countdown <= 1) {
                    clearInterval(this.countdownInterval);
                    return { canResend: true, countdown: 0 };
                }
                return { countdown: prevState.countdown - 1 };
            });
        }, 1000);
    };

    handleSendCode = async () => {
        const { email } = this.state;
        if (!email) {
            this.setState({ error: 'Введите email' });
            return;
        }

        this.setState({ isLoading: true, error: null });

        try {
            await post('/api/auth/SendCode',
                {
                    login: email,
                    purpose: 1
                });

            this.startCountdown();
            this.setState({ step: 2 });
        } catch (error) {
            let errorMessage;
            if (error.name === 'TypeError' || error.message === 'Failed to fetch') {
                errorMessage = 'Нет соединения с сервером. Проверьте интернет.';
            } else {
                errorMessage = error.message || 'Ошибка при отправке кода. Попробуйте повторить попытку позже';
            }
            this.setState({ error: errorMessage });
            console.error('Ошибка:', error);
        } finally {
            this.setState({ isLoading: false });
        }
    };

    handleResendCode = async () => {
        if (!this.state.canResend) return;
        await this.handleSendCode();
    };

    handleVerifyCode = async () => {
        const { email, code } = this.state;
        if (!code) {
            this.setState({ error: 'Введите код подтверждения' });
            return;
        }

        this.setState({ isLoading: true, error: null });

        try {
            await post('/api/auth/CheckCode',
                {
                    login: email.trim(),
                    code: code.trim(),
                    purpose: 1,
                });

            this.setState({ step: 3 });
        } catch (error) {
            let errorMessage;
            console.log(error);
            if (error.name === 'TypeError' || error.message === 'Failed to fetch') {
                errorMessage = 'Нет соединения с сервером. Проверьте интернет.';
            } else {
                errorMessage = error.message || 'Ошибка при подтверждении кода. Попробуйте повторить попытку позже';
            }
            this.setState({ error: errorMessage });
            console.error('Ошибка:', error);
        } finally {
            this.setState({ isLoading: false });
        }
    };

    checkPasswordComplexity(password) {
        const categories = {
            hasLower: /[a-z]/.test(password),
            hasUpper: /[A-Z]/.test(password),
            hasNumber: /[0-9]/.test(password),
            hasSpecial: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(password)
        };

        const passedCategories = Object.values(categories).filter(Boolean).length;
        return passedCategories >= 3;
    }

    handleResetPassword = async () => {
        const { email, newPassword, confirmPassword } = this.state;

        if (!newPassword || !confirmPassword) {
            this.setState({ error: 'Заполните оба поля пароля' });
            return;
        }

        if (!this.checkPasswordComplexity(newPassword) || !this.checkPasswordComplexity(confirmPassword)) {
            this.setState({
                error: 'Пароль должен содержать минимум 3 из 4 категорий: большие латинские буквы, маленькие латинские буквы, цифры и спецсимволы'
            });
            return;
        }

        if (newPassword.length < 8) {
            this.setState({ error: 'Пароль должен содержать минимум 8 символов' });
            return;
        }

        if (newPassword !== confirmPassword) {
            this.setState({ error: 'Пароли не совпадают' });
            return;
        }

        const username = email.split('@')[0].toLowerCase();
        if (newPassword.toLowerCase().includes(username)) {
            this.setState({ error: 'Пароль не должен содержать имя (email) пользователя' });
            return;
        }

        this.setState({ isLoading: true, error: null });

        try {
            await put('/api/auth/ChangePassword',
                {
                    login: email,
                    password: newPassword
                });
            this.setState({ success: true });
        } catch (error) {
            let errorMessage;
            if (error.name === 'TypeError' || error.message === 'Failed to fetch') {
                errorMessage = 'Нет соединения с сервером. Проверьте интернет.';
            } else {
                errorMessage = error.message || 'Ошибка при сбросе пароля. Попробуйте повторить попытку позже';
            }
            this.setState({ error: errorMessage });
            console.error('Ошибка:', error);
        } finally {
            this.setState({ isLoading: false });
        }
    };

    formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    componentDidUpdate(prevProps) {
        if (!prevProps.isOpen && this.props.isOpen) {
            this.resetState();
        }
    }

    resetState = () => {
        clearInterval(this.countdownInterval);
        this.setState({
            email: '',
            code: '',
            newPassword: '',
            confirmPassword: '',
            showNewPassword: false,
            showConfirmPassword: false,
            step: 1,
            isLoading: false,
            error: null,
            success: false,
            canResend: true,
            countdown: 0,
            lastSentTime: null
        });
    };

    renderStep = () => {
        const {
            step,
            email,
            code,
            newPassword,
            confirmPassword,
            canResend,
            countdown,
            showNewPassword,
            showConfirmPassword
        } = this.state;

        switch (step) {
            case 1:
                return (
                    <FormGroup>
                        <Label>Email</Label>
                        <Input
                            className="text-input standard-text"
                            type="email"
                            name="email"
                            value={email}
                            onChange={this.handleInputChange}
                            placeholder="Ваш email" />
                    </FormGroup>
                );
            case 2:
                return (
                    <>
                        <FormGroup>
                            <Label>Код из письма</Label>
                            <Input
                                className="text-input standard-text"
                                type="text"
                                name="code"
                                value={code}
                                onChange={this.handleInputChange}
                                placeholder="Введите код" />
                        </FormGroup>
                        <div className="text-center mt-3">
                            {!canResend ? (
                                <span className="text-muted">
                                    Повторная отправка через {this.formatTime(countdown)}
                                </span>
                            ) : (
                                <Button
                                    color="link"
                                    onClick={this.handleResendCode}
                                    disabled={!canResend}>
                                    Отправить код повторно
                                </Button>
                            )}
                        </div>
                    </>
                );
            case 3:
                return (
                    <>
                        <FormGroup>
                            <Label for="new-password">Новый пароль</Label>
                            <PasswordInput
                                id="new-password"
                                showPassword={showNewPassword}
                                toggleShowPassword={() => this.toggleShowPassword('NewPassword')}
                                password={newPassword}
                                handleInputChange={this.handleInputChange}
                                name="newPassword"
                                placeholder="Придумайте пароль" />
                            <small className="form-text text-muted">
                                Пароль должен содержать минимум 8 символов (латинские буквы, цифры и специальные символы)
                            </small>
                        </FormGroup>
                        <FormGroup>
                            <Label for="confirm-password">Подтверждение пароля</Label>
                            <PasswordInput
                                id="confirm-password"
                                showPassword={showConfirmPassword}
                                toggleShowPassword={() => this.toggleShowPassword('ConfirmPassword')}
                                password={confirmPassword}
                                handleInputChange={this.handleInputChange}
                                name="confirmPassword"
                                placeholder="Повторите пароль" />
                        </FormGroup>
                    </>
                );
            default:
                return null;
        }
    };

    render() {
        const { isOpen, onClose } = this.props;
        const { step, isLoading, error, success } = this.state;

        return (
            <Modal isOpen={isOpen} toggle={onClose}>
                <ModalHeader toggle={onClose}>Восстановление пароля</ModalHeader>
                <ModalBody>
                    {error && <Alert color="danger">{error}</Alert>}
                    {success ? (
                        <Alert color="success">Пароль успешно изменен!</Alert>
                    ) : (
                        this.renderStep()
                    )}
                </ModalBody>
                <ModalFooter>
                    {!success && (
                        <Button
                            className="dt1520-button active-dt1520-button view-button blue-button-with-borders"
                            onClick={
                                step === 1 ? this.handleSendCode :
                                    step === 2 ? this.handleVerifyCode :
                                        this.handleResetPassword
                            }
                            disabled={isLoading}>
                            {isLoading ? 'Отправка...' :
                                step === 1 ? 'Отправить код' :
                                    step === 2 ? 'Подтвердить' : 'Изменить пароль'}
                        </Button>
                    )}
                    <Button className="dt1520-button active-dt1520-button view-button default-button" onClick={onClose}>
                        {success ? 'Закрыть' : 'Отмена'}
                    </Button>
                </ModalFooter>
            </Modal>
        );
    }
}

export default PasswordResetModal;